import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-hot-toast";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { FaEdit } from "react-icons/fa";
import { HiOutlineArrowLeft, HiOutlineAcademicCap, HiOutlineDocumentText, HiOutlineClock, HiOutlineChevronRight, HiOutlineArrowRight, HiOutlineCheckCircle } from "react-icons/hi2";
import { MdCheckCircle, MdPlayCircleFilled } from "react-icons/md";
import { useSelector } from "react-redux";

import { WorkshopCard } from "../../../components/WorkshopCard";
import useWorkshopProgressStore from "../../../stores/workshopProgressStore";

import Loader from "@/components/loader";

import api from "@/services/api";

export default function View() {
  const [workshops, setWorkshops] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [training, setTraining] = useState(null);
  const [careerPath, setCareerPath] = useState(null);
  const [userWorkshops, setUserWorkshops] = useState([]);
  const navigate = useNavigate();
  const user = useSelector((state) => state.Auth.user);
  const { progress } = useWorkshopProgressStore();

  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const pathId = searchParams.get("pathId");

  useEffect(() => {
    getTraining();
    getWorkshops();
    if (user) getUserWorkshops();
    if (pathId) getCareerPath();
  }, [id, pathId, user]);

  useEffect(() => {
    if (user || workshops.length === 0) return;
    const localProgress = workshops
      .map((w) => {
        const p = progress[w.workshop_id];
        if (!p) return null;
        return { workshop_id: w.workshop_id, step_id: p.step_id, finished_at: p.finished_at };
      })
      .filter(Boolean);
    if (localProgress.length > 0) setUserWorkshops(localProgress);
  }, [workshops, user]);

  const getTraining = async () => {
    try {
      const { data, ok } = await api.get(`/training/${id}`);
      if (!ok) throw new Error("Failed to fetch training");
      setTraining(data);
    } catch (error) {
      toast.error("Failed to fetch training details");
    }
  };

  const getWorkshops = async () => {
    try {
      setIsLoading(true);
      const { data, ok } = await api.post("/training_workshop/search", { training_id: id });
      if (!ok) throw new Error("Failed to fetch workshops");
      setWorkshops(data);
    } catch (error) {
      toast.error("Failed to fetch workshops");
    } finally {
      setIsLoading(false);
    }
  };

  const getUserWorkshops = async () => {
    try {
      const { data, ok } = await api.get(`/workshop_user`);
      if (ok) setUserWorkshops(data || []);
    } catch (error) {
      // Silently fail
    }
  };

  const getCareerPath = async () => {
    try {
      const { data, ok } = await api.get(`/career_path/${pathId}`);
      if (ok) setCareerPath(data);
    } catch (error) {
      // Silently fail — we just won't show the path context
    }
  };

  // Helper to get user progress for a workshop
  const getWorkshopProgress = (workshopId) => {
    const uw = userWorkshops.find((u) => u.workshop_id === workshopId);
    if (!uw) return { status: "not_started", userWorkshop: null };
    if (uw.finished_at) return { status: "completed", userWorkshop: uw };
    return { status: "in_progress", userWorkshop: uw };
  };

  // Career path context: sorted courses, current index, prev/next
  const pathContext = useMemo(() => {
    if (!careerPath || !training) return null;
    const courses = (careerPath.trainings || []).slice().sort((a, b) => (a.training_position || 0) - (b.training_position || 0));
    const currentIndex = courses.findIndex((c) => c.training_id === id);
    if (currentIndex === -1) return null;
    return {
      courses,
      currentIndex,
      total: courses.length,
      prev: currentIndex > 0 ? courses[currentIndex - 1] : null,
      next: currentIndex < courses.length - 1 ? courses[currentIndex + 1] : null,
    };
  }, [careerPath, training, id]);

  const getSortedWorkshopChapters = () => {
    if (!training?.chapter_order) return [];

    const chapterMap = new Map();
    workshops.forEach((workshop) => {
      if (!chapterMap.has(workshop.chapter)) {
        chapterMap.set(workshop.chapter, []);
      }
      chapterMap.get(workshop.chapter).push(workshop);
    });

    chapterMap.forEach((workshops, chapter) => {
      workshops.sort((a, b) => a.position - b.position);
    });

    return training.chapter_order.filter((chapter) => chapterMap.has(chapter)).map((chapter) => [chapter, chapterMap.get(chapter)]);
  };

  function WorkshopSection({ chapter, workshops }) {
    const chapterCompleted = workshops.filter((w) => getWorkshopProgress(w.workshop_id).status === "completed").length;
    const chapterTotal = workshops.length;

    return (
      <div key={`${chapter}-${workshops.map((w) => w._id).join("-")}`} className="space-y-4">
        <div className="flex items-center mb-6">
          <div className="flex items-center gap-4 flex-1">
            <div className="w-8 h-8 bg-primary-100 rounded-xl flex items-center justify-center">
              <HiOutlineDocumentText className="w-4 h-4 text-primary-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">{chapter}</h3>
            <div className="h-px flex-1 bg-gradient-to-r from-gray-200 to-transparent" />
            <span className="text-sm text-gray-500 font-medium">
              {chapterCompleted > 0 && (
                <span className={`mr-2 ${chapterCompleted === chapterTotal ? "text-green-600" : "text-blue-600"}`}>
                  {chapterCompleted}/{chapterTotal} done
                </span>
              )}
              {workshops.length} workshops
            </span>
          </div>
        </div>

        {workshops.length > 0 && (
          <div className="grid gap-3">
            {workshops.map((workshop, index) => {
              const progress = getWorkshopProgress(workshop.workshop_id);
              const isCompleted = progress.status === "completed";
              const isInProgress = progress.status === "in_progress";

              return (
                <div key={workshop._id} className="relative">
                  {index > 0 && (
                    <div className={`absolute -top-1.5 left-4 w-px h-3 ${isCompleted ? "bg-green-300" : "bg-gray-200"}`}></div>
                  )}
                  <div className="flex items-center gap-4">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold
                      ${isCompleted ? "bg-green-100 text-green-700" : isInProgress ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-500"}`}>
                      {isCompleted ? <MdCheckCircle className="w-5 h-5 text-green-600" /> : index + 1}
                    </div>
                    <div
                      className="flex-1 cursor-pointer transition-all duration-200"
                      onClick={() => {
                        const params = new URLSearchParams();
                        if (pathId) params.set("pathId", pathId);
                        params.set("courseId", id);
                        if (isInProgress && progress.userWorkshop?.step_id) {
                          params.set("index", progress.userWorkshop.step_id);
                        }
                        navigate(`/selecole/learn/${workshop.workshop_id}?${params.toString()}`);
                      }}>
                      <div className={`group rounded-2xl shadow-sm border transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5
                        ${isCompleted ? "bg-green-50/50 border-green-200 hover:border-green-300" : isInProgress ? "bg-blue-50/30 border-blue-200 hover:border-blue-300" : "bg-white border-gray-200 hover:border-gray-300"}`}>
                        <div className="flex items-center">
                          <div className="flex-1">
                            <WorkshopCard workshop={workshop} />
                          </div>
                          <div className="pr-4 flex-shrink-0">
                            {isCompleted && (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                <HiOutlineCheckCircle className="w-3.5 h-3.5" /> Completed
                              </span>
                            )}
                            {isInProgress && (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                <MdPlayCircleFilled className="w-3.5 h-3.5" /> Continue
                              </span>
                            )}
                            {progress.status === "not_started" && (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity">
                                Start →
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (isLoading) {
    return <Loader color="black" />;
  }

  if (!training) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl shadow-lg border border-gray-200 p-12 max-w-md mx-auto">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <HiOutlineAcademicCap className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Course Not Found</h2>
          <p className="text-gray-600 mb-6">The course you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate("/selecole/course")}
            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2.5 rounded-xl font-medium transition-colors duration-200">
            Back to Courses
          </button>
        </div>
      </div>
    );
  }

  const sortedChapters = getSortedWorkshopChapters();
  const totalWorkshops = workshops.length;
  const completedWorkshops = workshops.filter((w) => getWorkshopProgress(w.workshop_id).status === "completed").length;
  const inProgressWorkshops = workshops.filter((w) => getWorkshopProgress(w.workshop_id).status === "in_progress").length;
  const courseProgress = totalWorkshops > 0 ? Math.round((completedWorkshops / totalWorkshops) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-6xl mx-auto p-6">
        {/* ── Breadcrumb / Back Navigation ── */}
        {pathContext && careerPath ? (
          <nav className="flex items-center gap-2 text-sm mb-6 flex-wrap">
            <button
              onClick={() => navigate("/selecole/career-path")}
              className="text-gray-500 hover:text-gray-800 transition-colors font-medium">
              Career Paths
            </button>
            <HiOutlineChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <button
              onClick={() => navigate(`/selecole/career-path/${pathId}`)}
              className="text-primary-600 hover:text-primary-800 transition-colors font-medium truncate max-w-[200px]">
              {careerPath.name}
            </button>
            <HiOutlineChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <span className="text-gray-900 font-semibold truncate max-w-[250px]">{training.title}</span>
          </nav>
        ) : (
          user && (
            <button onClick={() => navigate("/selecole/course")} className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors duration-200 group">
              <HiOutlineArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-200" />
              <span className="font-medium">Back to Courses</span>
            </button>
          )
        )}

        {/* ── Career Path Progress Bar ── */}
        {pathContext && careerPath && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-primary-100 rounded-xl flex items-center justify-center">
                  <HiOutlineAcademicCap className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-primary-600 uppercase tracking-wider">{careerPath.name}</div>
                  <div className="text-sm text-gray-600">
                    Course {pathContext.currentIndex + 1} of {pathContext.total}
                  </div>
                </div>
              </div>
            </div>

            {/* Stepper dots */}
            <div className="flex items-center gap-0">
              {pathContext.courses.map((course, i) => {
                const isCurrent = i === pathContext.currentIndex;
                return (
                  <React.Fragment key={course.training_id}>
                    <button
                      onClick={() => {
                        if (!isCurrent) navigate(`/selecole/course/${course.training_id}?pathId=${pathId}`);
                      }}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 flex-shrink-0 ${
                        isCurrent
                          ? "bg-primary-600 text-white shadow-sm"
                          : "bg-gray-100 text-gray-600 hover:bg-primary-50 hover:text-primary-700 cursor-pointer"
                      }`}
                      title={course.training_title}>
                      <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-[11px] font-bold">
                        {i + 1}
                      </span>
                      <span className="hidden sm:inline truncate max-w-[120px]">{course.training_title}</span>
                    </button>
                    {i < pathContext.courses.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-1 min-w-[8px] ${i < pathContext.currentIndex ? "bg-primary-300" : "bg-gray-200"}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Header Section ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-8">
          <div className="flex justify-between items-start gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <HiOutlineAcademicCap className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 mb-1">Course</span>
                </div>
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4 leading-tight">{training.title}</h1>
              {training.description && <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mb-6">{training.description}</p>}
              <div className="flex items-center gap-6 mb-4">
                {training.duration && (
                  <div className="flex items-center text-sm text-gray-500">
                    <HiOutlineClock className="w-5 h-5 mr-2 text-blue-500" />
                    <span className="font-medium">{training.duration}</span>
                  </div>
                )}
                <div className="flex items-center text-sm text-gray-500">
                  <HiOutlineDocumentText className="w-5 h-5 mr-2 text-primary-500" />
                  <span className="font-medium">
                    {totalWorkshops} {totalWorkshops === 1 ? "Workshop" : "Workshops"}
                  </span>
                </div>
                {completedWorkshops > 0 && (
                  <div className="flex items-center text-sm text-green-600">
                    <HiOutlineCheckCircle className="w-5 h-5 mr-2" />
                    <span className="font-medium">{completedWorkshops} completed</span>
                  </div>
                )}
              </div>

              {/* Course progress bar */}
              {totalWorkshops > 0 && (completedWorkshops > 0 || inProgressWorkshops > 0) && (
                <div className="max-w-md">
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="text-gray-600 font-medium">Your progress</span>
                    <span className={`font-semibold ${courseProgress === 100 ? "text-green-600" : "text-blue-600"}`}>{courseProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className={`h-2.5 rounded-full transition-all duration-500 ${courseProgress === 100 ? "bg-green-500" : "bg-blue-500"}`}
                      style={{ width: `${courseProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1.5">
                    {completedWorkshops}/{totalWorkshops} workshops completed
                    {inProgressWorkshops > 0 && ` · ${inProgressWorkshops} in progress`}
                  </p>
                </div>
              )}
              {!user && (
                <a
                  href={`/auth?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                  className="inline-flex items-center gap-2 mt-2 text-sm text-primary-600 hover:text-primary-800 font-medium transition-colors">
                  Login to track your progress →
                </a>
              )}
            </div>
            {["admin", "super_admin"].includes(user?.role) && (
              <button
                onClick={() => navigate(`/selecole/course/${id}/edit`)}
                className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-5 py-2.5 rounded-xl text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200 flex-shrink-0">
                <FaEdit className="w-4 h-4" />
                Edit Course
              </button>
            )}
          </div>
        </div>

        {/* ── Course Content ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Course Content</h2>
            <p className="text-gray-600">Explore the workshops below. Each workshop is designed to build upon the previous one, creating a comprehensive learning experience.</p>
          </div>

          {sortedChapters.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <HiOutlineDocumentText className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Workshops Available</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">This course doesn't have any workshop content yet. Check back later or contact an administrator.</p>
              {["admin", "super_admin"].includes(user?.role) && (
                <button
                  onClick={() => navigate(`/selecole/course/${id}/edit`)}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2.5 rounded-xl font-medium transition-colors duration-200">
                  Add Workshops
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-12">
              {sortedChapters.map(([chapter, chapterWorkshops]) => (
                <WorkshopSection key={`${chapter}-section`} chapter={chapter} workshops={chapterWorkshops} />
              ))}
            </div>
          )}
        </div>

        {/* ── Previous / Next Course Navigation ── */}
        {pathContext && (
          <div className="mt-8 grid grid-cols-2 gap-4">
            {/* Previous course */}
            {pathContext.prev ? (
              <button
                onClick={() => navigate(`/selecole/course/${pathContext.prev.training_id}?pathId=${pathId}`)}
                className="group bg-white rounded-2xl shadow-sm border border-gray-200 p-5 text-left hover:border-primary-300 hover:shadow-md transition-all duration-200">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">← Previous course</div>
                <div className="font-semibold text-gray-900 group-hover:text-primary-700 transition-colors line-clamp-1">
                  {pathContext.prev.training_title}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Course {pathContext.currentIndex} of {pathContext.total}
                </div>
              </button>
            ) : (
              <div />
            )}

            {/* Next course */}
            {pathContext.next ? (
              <button
                onClick={() => navigate(`/selecole/course/${pathContext.next.training_id}?pathId=${pathId}`)}
                className="group bg-primary-50 rounded-2xl shadow-sm border border-primary-200 p-5 text-right hover:bg-primary-100 hover:shadow-md transition-all duration-200">
                <div className="text-xs font-semibold text-primary-500 uppercase tracking-wider mb-2">Next course →</div>
                <div className="font-semibold text-gray-900 group-hover:text-primary-700 transition-colors line-clamp-1">
                  {pathContext.next.training_title}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Course {pathContext.currentIndex + 2} of {pathContext.total}
                </div>
              </button>
            ) : (
              <button
                onClick={() => navigate(`/selecole/career-path/${pathId}`)}
                className="group bg-green-50 rounded-2xl shadow-sm border border-green-200 p-5 text-right hover:bg-green-100 hover:shadow-md transition-all duration-200">
                <div className="text-xs font-semibold text-green-600 uppercase tracking-wider mb-2">Finish</div>
                <div className="font-semibold text-gray-900 group-hover:text-green-700 transition-colors">
                  Back to Career Path
                </div>
                <div className="text-xs text-gray-500 mt-1">You've reached the last course!</div>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
